import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getCourseDetailById, getCourseLessons } from '../../api/courses';
import { SEO } from '../../components/SEO';
import { CustomVideoPlayer } from './CustomVideoPlayer';
import './CoursePlayer.scss';

export const CourseDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((s) => s.auth || {});

  const [courseData, setCourseData] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const queryParams = new URLSearchParams(location.search);
  const lessonIdParam = queryParams.get('lessonId');
  
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [openModules, setOpenModules] = useState(['mod-1']);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch real course data
        const res = await getCourseDetailById(id);
        let cData = res && res.data ? res.data : null;
        if (!cData && res && !res.status) cData = res; // fallback if it's the root object and not just a status wrapper
        
        if (cData) {
          setCourseData(cData);
        } else {
          setCourseData({ title: 'Course Details', category_name: 'Education' });
        }
        
        // Fetch lessons
        const lessonsRes = await getCourseLessons(id);
        let fetchedLessons = [];
        if (lessonsRes && lessonsRes.data) {
          fetchedLessons = lessonsRes.data;
        } else if (Array.isArray(lessonsRes)) {
          fetchedLessons = lessonsRes;
        }

        // Group lessons into modules dynamically
        const dynamicModules = [];
        if (fetchedLessons.length > 0) {
          fetchedLessons.forEach((lesson, index) => {
            const modName = lesson.module_name || lesson.module || `Module ${Math.floor(index / 5) + 1}`;
            let mod = dynamicModules.find((m) => m.title === modName);
            if (!mod) {
              mod = {
                id: `mod-${dynamicModules.length + 1}`,
                title: modName,
                completedCount: 0,
                totalCount: 0,
                lessons: [],
              };
              dynamicModules.push(mod);
            }
            const isCompleted = lesson.isVideoCompleted || lesson.is_completed || lesson.completed;
            if (isCompleted) mod.completedCount++;
            mod.totalCount++;
            
            const isLocked = lesson.is_enable_next === false; 
            
            let status = 'locked';
            if (isCompleted) status = 'completed';
            else if (!isLocked) status = 'circle';

            mod.lessons.push({
              ...lesson,
              id: lesson._id || lesson.id || `l-${index}`,
              num: `${mod.lessons.length + 1}.`,
              title: lesson.title,
              duration: lesson.duration || lesson.videoDuration || '05:00',
              status: status,
              originalStatus: status
            });
          });
        }
        
        if (dynamicModules.length > 0) {
          // Set first unlocked as active, or just first lesson
          const firstUnlocked = dynamicModules[0].lessons[0];
          if (firstUnlocked) {
             firstUnlocked.status = 'active';
             setSelectedLesson(firstUnlocked);
          }
          setLessons(dynamicModules);
          setOpenModules([dynamicModules[0].id]);
        } else {
           setLessons([]);
        }
        
      } catch (err) {
        console.error("Failed to load course player data:", err);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchData();
    }
  }, [id, lessonIdParam]);

  if (loading || !courseData) {
    return <div className="course-player-page justify-content-center align-items-center bg-dark text-white d-flex"><div className="spinner-border text-yellow"></div></div>;
  }

  const categoryName = courseData.category_name || 'Education';
  const lessonTitle = selectedLesson ? `${selectedLesson.num} ${selectedLesson.title}` : '1. Self-Introduction';

  const toggleModule = (modId) => {
    setOpenModules(prev => 
      prev.includes(modId) ? prev.filter(id => id !== modId) : [...prev, modId]
    );
  };

  return (
    <div className="course-player-page">
      <SEO title={lessonTitle} />
      
      {/* Top Navigation */}
      <div className="player-top-nav">
        <div className="breadcrumbs">
          <Link to="/courses">Courses</Link>
          <i className="bi bi-chevron-right"></i>
          <Link to="/courses">{categoryName}</Link>
          <i className="bi bi-chevron-right"></i>
          <Link to={`/course-detail/${id}`}>{courseData.title}</Link>
          <i className="bi bi-chevron-right"></i>
          <span className="active">{lessonTitle}</span>
        </div>
        <div className="top-actions">
          <button className="btn-nav-action"><i className="bi bi-file-earmark-text"></i> Notes</button>
          <button className="btn-nav-action"><i className="bi bi-arrows-angle-expand"></i> Fullscreen</button>
          <button className="btn-nav-action" onClick={() => navigate(`/courses`)}><i className="bi bi-box-arrow-right"></i> Exit Course</button>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="player-main-content">
        <Row className="g-4 m-0">
          
          {/* Left Column - Player & Details */}
          <Col xl={8} lg={8} md={8} className="p-0 pe-md-3">
            
            {/* Video Container */}
            <div className="video-container">
              {(() => {
                const activeVideoUrl = selectedLesson ? (selectedLesson.video_url || selectedLesson.media || selectedLesson.videoUrl) : (courseData.coverVideo || courseData.cover_video);
                
                if (activeVideoUrl) {
                  const fallbackImg = courseData?.cover_image || courseData?.coverImage || courseData?.image || "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=800&h=450&fit=crop";
                  const isYouTube = activeVideoUrl.includes('youtube.com') || activeVideoUrl.includes('youtu.be');
                  if (isYouTube) {
                     const match = activeVideoUrl.match(/(?:youtu\.be\/|v=)([^?&]+)/);
                     const embedUrl = match ? `https://www.youtube.com/embed/${match[1]}` : activeVideoUrl;
                     return <iframe src={embedUrl} title="Video" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" style={{ width: '100%', height: '100%', border: 'none' }} />;
                  }
                  return <CustomVideoPlayer url={activeVideoUrl} title={lessonTitle} poster={fallbackImg} />;
                }
                
                // Fallback image if no video
                const fallbackImg = courseData?.cover_image || courseData?.coverImage || courseData?.image || "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?w=800&h=450&fit=crop";
                return (
                  <div style={{ position: 'relative', width: '100%', height: '100%', backgroundImage: `url("${fallbackImg}")`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)' }}></div>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: 'white', textAlign: 'center' }}>
                       <i className="bi bi-camera-video-off" style={{fontSize: '3rem', opacity: 0.5}}></i>
                       <p className="mt-2 text-white-50">No video available</p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Lesson Header */}
            <div className="lesson-header">
              <div className="title-area">
                <h2>{lessonTitle}</h2>
                <div className="lesson-meta">
                  <div className="meta-item"><i className="bi bi-clock"></i> 08:45 min</div>
                  <div className="meta-item"><i className="bi bi-camera-video"></i> Video Lesson</div>
                  <div className="meta-item"><i className="bi bi-reception-4"></i> Beginner</div>
                </div>
              </div>
              <div className="lesson-actions">
                <button className="btn-action btn-mark-done">
                  <i className="bi bi-check-lg"></i> Mark as Done
                </button>
                <button className="btn-action btn-outline">
                  <i className="bi bi-bookmark"></i> Add to Watch Later
                </button>
                <button className="btn-action btn-outline">
                  <i className="bi bi-share"></i> Share Lesson
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="custom-tabs-container">
              <div className="custom-tabs">
                <button className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
                <button className={`tab-btn ${activeTab === 'resources' ? 'active' : ''}`} onClick={() => setActiveTab('resources')}>Resources</button>
                <button className={`tab-btn ${activeTab === 'qa' ? 'active' : ''}`} onClick={() => setActiveTab('qa')}>Q&A (24)</button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <Row className="g-4 m-0">
                <Col md={7} className="p-0 pe-md-2">
                  <div className="info-card">
                    <h5>About This Lesson</h5>
                    <p>In this lesson, you'll learn how to introduce yourself in English with confidence. We'll go over common expressions, examples, and a simple practice to help you speak naturally.</p>
                    
                    <Row className="mt-4 m-0">
                      <Col sm={6} className="p-0 pe-2">
                        <h6>What You'll Learn</h6>
                        <ul className="skills-list">
                          <li><div className="icon-circle"><i className="bi bi-check-lg"></i></div> How to introduce yourself in English</li>
                          <li><div className="icon-circle"><i className="bi bi-check-lg"></i></div> Common phrases and expressions</li>
                          <li><div className="icon-circle"><i className="bi bi-check-lg"></i></div> Real-life examples</li>
                          <li><div className="icon-circle"><i className="bi bi-check-lg"></i></div> Practice speaking with confidence</li>
                        </ul>
                      </Col>
                      <Col sm={6} className="p-0 ps-2">
                        <h6>Skills You'll Gain</h6>
                        <ul className="skills-list">
                          <li><i className="bi bi-person-video icon-plain"></i> Speaking Confidence</li>
                          <li><i className="bi bi-chat-square-text icon-plain"></i> Self-Expression</li>
                          <li><i className="bi bi-headphones icon-plain"></i> Listening Comprehension</li>
                          <li><i className="bi bi-journal-text icon-plain"></i> Vocabulary Building</li>
                        </ul>
                      </Col>
                    </Row>
                  </div>
                </Col>
                
                <Col md={5} className="p-0 ps-md-2">
                  <div className="info-card mb-3 pb-2 pt-3">
                    <h5 className="mb-2">Lesson Resources</h5>
                    <div className="resource-item">
                      <div className="r-info">
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <div>
                          <div className="r-name">Lesson Transcript (PDF)</div>
                          <div className="r-type">PDF Document</div>
                        </div>
                      </div>
                      <button className="btn-download">Download</button>
                    </div>
                    <div className="resource-item">
                      <div className="r-info">
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <div>
                          <div className="r-name">Vocabulary List</div>
                          <div className="r-type">PDF Document</div>
                        </div>
                      </div>
                      <button className="btn-download">Download</button>
                    </div>
                    <div className="resource-item">
                      <div className="r-info">
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <div>
                          <div className="r-name">Practice Worksheet</div>
                          <div className="r-type">PDF Document</div>
                        </div>
                      </div>
                      <button className="btn-download">Download</button>
                    </div>
                    <div className="resource-item border-0">
                      <div className="r-info">
                        <i className="bi bi-file-earmark-text-fill"></i>
                        <div>
                          <div className="r-name">Extra Examples</div>
                          <div className="r-type">PDF Document</div>
                        </div>
                      </div>
                      <button className="btn-download">Download</button>
                    </div>
                  </div>
                  
                  <div className="info-card pb-3 pt-3">
                    <h6 className="mb-3">Lesson Tags</h6>
                    <div className="tags-container">
                      <span className="tag-pill">Self-Introduction</span>
                      <span className="tag-pill">Speaking</span>
                      <span className="tag-pill">Beginner</span>
                      <span className="tag-pill">Vocabulary</span>
                      <span className="tag-pill">Confidence</span>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            {activeTab === 'resources' && (
              <div className="info-card">
                <h5 className="mb-4">All Course Resources</h5>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="resource-item border rounded p-3" style={{borderColor: '#1f2937'}}>
                      <div className="r-info">
                        <i className="bi bi-file-earmark-pdf-fill" style={{color: '#ef4444'}}></i>
                        <div>
                          <div className="r-name">Module 1 Full Guide</div>
                          <div className="r-type">PDF Document • 2.4 MB</div>
                        </div>
                      </div>
                      <button className="btn-download"><i className="bi bi-download"></i></button>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="resource-item border rounded p-3" style={{borderColor: '#1f2937'}}>
                      <div className="r-info">
                        <i className="bi bi-file-earmark-zip-fill" style={{color: '#ffc107'}}></i>
                        <div>
                          <div className="r-name">Practice Audio Files</div>
                          <div className="r-type">ZIP Archive • 15 MB</div>
                        </div>
                      </div>
                      <button className="btn-download"><i className="bi bi-download"></i></button>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="resource-item border rounded p-3" style={{borderColor: '#1f2937'}}>
                      <div className="r-info">
                        <i className="bi bi-file-earmark-pdf-fill" style={{color: '#ef4444'}}></i>
                        <div>
                          <div className="r-name">Conversation Cheat Sheet</div>
                          <div className="r-type">PDF Document • 1.1 MB</div>
                        </div>
                      </div>
                      <button className="btn-download"><i className="bi bi-download"></i></button>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="resource-item border rounded p-3" style={{borderColor: '#1f2937'}}>
                      <div className="r-info">
                        <i className="bi bi-link-45deg" style={{color: '#3b82f6'}}></i>
                        <div>
                          <div className="r-name">External Tool Reference</div>
                          <div className="r-type">Web Link</div>
                        </div>
                      </div>
                      <button className="btn-download"><i className="bi bi-box-arrow-up-right"></i></button>
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {activeTab === 'qa' && (
              <div className="info-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="mb-0">Questions & Answers (24)</h5>
                  <button className="btn btn-primary" style={{backgroundColor: '#4f46e5', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 600}}>
                    Ask a new question
                  </button>
                </div>
                
                <div className="qa-search mb-4 position-relative">
                  <i className="bi bi-search position-absolute" style={{top: '50%', transform: 'translateY(-50%)', left: '1rem', color: '#9ca3af'}}></i>
                  <input type="text" placeholder="Search for a question..." className="form-control" style={{backgroundColor: '#1f2937', border: '1px solid #374151', color: 'white', paddingLeft: '2.5rem', borderRadius: '8px'}} />
                </div>

                <div className="qa-list d-flex flex-column gap-3">
                  {/* QA Item 1 */}
                  <div className="qa-item p-3 border rounded" style={{borderColor: '#1f2937', backgroundColor: 'rgba(255,255,255,0.02)'}}>
                    <div className="d-flex gap-3 mb-2">
                      <div style={{width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'}}>JS</div>
                      <div>
                        <div className="fw-bold" style={{fontSize: '0.9rem'}}>John Smith</div>
                        <div style={{fontSize: '0.75rem', color: '#9ca3af'}}>2 days ago</div>
                      </div>
                    </div>
                    <div className="fw-bold mb-1" style={{fontSize: '0.9rem'}}>How do I know when to use formal vs informal greetings?</div>
                    <p style={{fontSize: '0.85rem', color: '#d1d5db', marginBottom: '0.75rem'}}>I always get confused when speaking to someone older than me but in a casual setting.</p>
                    <div className="d-flex align-items-center gap-3" style={{fontSize: '0.8rem', color: '#9ca3af'}}>
                      <span style={{cursor: 'pointer'}}><i className="bi bi-hand-thumbs-up"></i> 12</span>
                      <span style={{cursor: 'pointer'}}><i className="bi bi-chat-dots"></i> 3 Replies</span>
                    </div>
                  </div>

                  {/* QA Item 2 */}
                  <div className="qa-item p-3 border rounded" style={{borderColor: '#1f2937', backgroundColor: 'rgba(255,255,255,0.02)'}}>
                    <div className="d-flex gap-3 mb-2">
                      <div style={{width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold'}}>ML</div>
                      <div>
                        <div className="fw-bold" style={{fontSize: '0.9rem'}}>Maria Lopez</div>
                        <div style={{fontSize: '0.75rem', color: '#9ca3af'}}>5 days ago</div>
                      </div>
                    </div>
                    <div className="fw-bold mb-1" style={{fontSize: '0.9rem'}}>Is there a transcript for this video?</div>
                    <p style={{fontSize: '0.85rem', color: '#d1d5db', marginBottom: '0.75rem'}}>I learn better when I can read along.</p>
                    <div className="d-flex align-items-center gap-3" style={{fontSize: '0.8rem', color: '#9ca3af'}}>
                      <span style={{cursor: 'pointer'}}><i className="bi bi-hand-thumbs-up"></i> 5</span>
                      <span style={{cursor: 'pointer'}}><i className="bi bi-chat-dots"></i> 1 Reply</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </Col>

          {/* Right Column - Curriculum */}
          <Col xl={4} lg={4} md={4} className="p-0 ps-md-3">
            <div className="sidebar-container">
              
              {/* Progress Card */}
              <div className="progress-card">
                {(() => {
                  const totalLessons = lessons.reduce((acc, mod) => acc + mod.totalCount, 0);
                  const completedLessons = lessons.reduce((acc, mod) => acc + mod.completedCount, 0);
                  const progressPct = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
                  return (
                    <>
                      <div className="progress-header">
                        <span>Course Progress</span>
                        <span>{progressPct}% Complete</span>
                      </div>
                      <div className="progress-bar-bg">
                        <div className="progress-bar-fill" style={{ width: `${progressPct}%` }}></div>
                      </div>
                      <div className="progress-meta">
                        {totalLessons} Lesson{totalLessons !== 1 ? 's' : ''} • {courseData.duration || '12h 30m'}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Modules List */}
              <div className="modules-list">
                {lessons.map((mod) => {
                  const isOpen = openModules.includes(mod.id);
                  return (
                    <div className="module-card" key={mod.id}>
                      <div className="module-header" onClick={() => toggleModule(mod.id)}>
                        <div className="mod-title">{mod.title}</div>
                        <div className="mod-meta">
                          <span>{mod.completedCount}/{mod.totalCount}</span>
                          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
                        </div>
                      </div>
                      
                      {isOpen && mod.lessons && (
                        <div className="module-lessons">
                          {mod.lessons.map((lesson) => {
                            const isActive = lesson.status === 'active';
                            return (
                              <div 
                                key={lesson.id} 
                                className={`lesson-item ${isActive ? 'active' : ''}`}
                                onClick={() => setSelectedLesson(lesson)}
                              >
                                <div className="lesson-title-area">
                                  <span className="lesson-num">{lesson.num}</span>
                                  {isActive && <i className="bi bi-play-circle-fill lesson-play-icon"></i>}
                                  <span className="lesson-title">{lesson.title}</span>
                                </div>
                                <div className="lesson-right">
                                  <span className="lesson-duration">{lesson.duration}</span>
                                  {lesson.status === 'completed' && <i className="bi bi-check-circle lesson-status-icon completed"></i>}
                                  {lesson.status === 'circle' && <i className="bi bi-circle lesson-status-icon"></i>}
                                  {lesson.status === 'locked' && <i className="bi bi-lock lesson-status-icon"></i>}
                                  {lesson.status === 'active' && <i className="bi bi-play-circle-fill lesson-status-icon" style={{opacity:0}}></i> /* hidden placeholder */}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          </Col>
        </Row>
      </div>
      
      {/* Full Width Bottom Navigation */}
      {(() => {
        const allLessons = lessons.reduce((acc, mod) => [...acc, ...mod.lessons], []);
        const currentLessonIdx = allLessons.findIndex(l => l.id === (selectedLesson && selectedLesson.id));
        const prevLesson = currentLessonIdx > 0 ? allLessons[currentLessonIdx - 1] : null;
        const nextLesson = currentLessonIdx >= 0 && currentLessonIdx < allLessons.length - 1 ? allLessons[currentLessonIdx + 1] : null;

        return (
          <div className="bottom-nav-container">
            <div className="nav-side">
              {prevLesson && (
                <>
                  <Link to="#" className="nav-btn-circle" onClick={(e) => { e.preventDefault(); setSelectedLesson(prevLesson); }}><i className="bi bi-chevron-left"></i></Link>
                  <div>
                    <div className="nav-text">Previous Lesson</div>
                    <div className="nav-title">{prevLesson.title}</div>
                    <div className="nav-text mt-1 d-flex align-items-center gap-1">{prevLesson.duration} {prevLesson.originalStatus === 'completed' && <i className="bi bi-check-circle status-icon"></i>}</div>
                  </div>
                </>
              )}
            </div>
            
            <div className="nav-center">
              {selectedLesson && selectedLesson.originalStatus === 'completed' ? (
                <div className="progress-msg">Great job! You've completed this lesson.</div>
              ) : (
                <div className="progress-msg">Keep going! You're making progress.</div>
              )}
              {nextLesson && (
                <button className="btn-next-main" onClick={() => setSelectedLesson(nextLesson)}>Next Lesson <i className="bi bi-chevron-right ms-2 fs-5"></i></button>
              )}
            </div>
            
            <div className="nav-side next">
              {nextLesson && (
                <>
                  <div>
                    <div className="nav-text">Next Lesson</div>
                    <div className="nav-title">{nextLesson.title}</div>
                    <div className="nav-text mt-1">{nextLesson.duration}</div>
                  </div>
                  <Link to="#" className="nav-btn-circle" onClick={(e) => { e.preventDefault(); setSelectedLesson(nextLesson); }}><i className="bi bi-chevron-right"></i></Link>
                </>
              )}
            </div>
          </div>
        );
      })()}

    </div>
  );
};
